'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

interface TemplatesFunilFormProps {
  variant?: 'hero' | 'bottom'
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length <= 2) return digits
  if (digits.length <= 7) return '(' + digits.slice(0, 2) + ') ' + digits.slice(2)
  if (digits.length <= 11)
    return '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 7) + '-' + digits.slice(7)
  return '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 7) + '-' + digits.slice(7, 11)
}

export function TemplatesFunilForm({ variant = 'hero' }: TemplatesFunilFormProps) {
  const router = useRouter()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !email.trim()) {
      setError('Preencha seu nome e e-mail.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const phoneDigits = phone.replace(/\D/g, '')
      const fullPhone = phoneDigits ? '+55' + phoneDigits : ''

      const payload: Record<string, string> = {
        nome: name.trim(),
        email: email.trim(),
        assunto: 'Lead magnet templates funil',
      }
      if (fullPhone) {
        payload.numero = fullPhone
      }

      await fetch(
        'https://api.funilchatbot.com.br/wh/lead-templates-funil',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      )

      router.push('/templates-funil/obrigado')
    } catch {
      setError('Algo deu errado. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const isBottom = variant === 'bottom'

  return (
    <div
      id="form"
      className={`bg-white rounded-2xl border border-stone-200 shadow-xl shadow-stone-200/50 p-6 sm:p-8 ${isBottom ? 'max-w-xl mx-auto' : ''}`}
    >
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 bg-teal-50 text-teal-700 rounded-full px-3 py-1 text-xs font-bold mb-3">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Acesso imediato
        </div>
        <h3 className="text-xl font-black text-stone-900 mb-1">
          Baixar os 32 Templates Grátis
        </h3>
        <p className="text-sm text-stone-500">
          Preencha abaixo e acesse os templates na hora.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor={`name-${variant}`} className="block text-sm font-semibold text-stone-700 mb-1.5">
            Seu nome
          </label>
          <input
            id={`name-${variant}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Como posso te chamar?"
            required
            className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all"
          />
        </div>

        <div>
          <label htmlFor={`email-${variant}`} className="block text-sm font-semibold text-stone-700 mb-1.5">
            Seu melhor e-mail
          </label>
          <input
            id={`email-${variant}`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@exemplo.com"
            required
            className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all"
          />
        </div>

        <div>
          <label htmlFor={`phone-${variant}`} className="block text-sm font-semibold text-stone-700 mb-1.5">
            WhatsApp <span className="text-stone-400 font-normal">(opcional)</span>
          </label>
          <div className="flex gap-2">
            <div className="flex items-center gap-1.5 px-3 py-3 bg-stone-100 border border-stone-200 rounded-xl text-sm text-stone-600 shrink-0">
              <span>+55</span>
            </div>
            <input
              id={`phone-${variant}`}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(11) 99999-9999"
              className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all"
            />
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 disabled:bg-stone-300 text-white font-bold rounded-xl transition-all text-base shadow-lg shadow-teal-600/20 cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Enviando...
            </>
          ) : (
            <>
              Quero os 32 Templates
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </>
          )}
        </button>

        <p className="text-xs text-stone-400 text-center">
          Seus dados estão seguros. Não enviamos spam.
        </p>
      </form>
    </div>
  )
}
